import axios, { AxiosError, AxiosResponse } from 'axios';
import { put, select, takeEvery, takeLeading } from 'redux-saga/effects';
import * as lessonActions from 'Actions/lesson';
import * as appActions from 'Actions/app';
import {
  CreateLevelSession,
  InitiateWrapUp,
  lessonActionTypes,
  UpdateCurrentULP,
  UpdateCurrentULPSuccess,
  PlaySessionData,
} from 'Types/LessonTypes';
import { LessonState } from 'Reducers/lessonReducer';
import { AppState, SetLevelSelect, UserLevelData, actionTypes } from 'Types';
import { EventState } from 'Types/EventTypes';
import StartingULP from 'Fixtures/UserLevelProgress/StartingULP';
import { awsRum } from 'Utils/AwsRum';
import logger from 'Utils/Logger';
import { has } from 'lodash';
import { startupTierCountFor, tierCountOf } from 'Utils/LevelTierCount';
import { selectEffectiveGrading, ungradedSessionFields } from 'Utils/playAlong/effectiveGrading';

// axiosRetry(axios,{ retryDelay: axiosRetry.linearDelay(), retries: 3 } )

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;

// MUS-651: a tiers request that has not answered in this long fails, so a
// play session being created is never held open behind one that will not.
export const TIERS_REQUEST_TIMEOUT_MS = 15000;

async function getTiersForLevel(levelNumber: number) {
  const options = {
    url:
      process.env.REACT_APP_BACKEND_URL +
      `/api/v1/phrases/tiers-by-level?levelNumber=${levelNumber}`,
    headers: {
      'Access-Control-Allow-Origin': '*',
    },
    timeout: TIERS_REQUEST_TIMEOUT_MS,
  };
  const response = await axios.request(options);
  return (response as { data: unknown })['data'];
}

export async function gettimeSigForLevel(levelNumber: number, tierNumber: number) {
  const options = {
    url:
      process.env.REACT_APP_BACKEND_URL +
      `/api/v1/phrases/time-sig-by-level-tier?levelNumber=${levelNumber}&tierNumber=${tierNumber}`,
    headers: {
      'Access-Control-Allow-Origin': '*',
    },
  };
  const response = await axios.request(options);
  return (response as { data: unknown })['data'];
}

export function* onLevelSelectUpdate(action: SetLevelSelect): Generator<unknown, void, unknown> {
  // MUS-651: a throw here escaped the takeEvery and took every lesson watcher
  // down with it for the rest of the app session (session creation, progress
  // updates, the wrap-up), so every lesson after it loaded nothing. A failed
  // request or a level index past the end only skips this refresh.
  try {
    const lesson: LessonState = yield select(({ lessonReducer }) => lessonReducer);
    const data: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
    const levelNumber = data.levelData[action.payload.levelSelect].level_number;
    // MUS-651: until this level's tier list answers, its count is the one
    // loaded at startup, or not known - never the last level's count. The
    // list itself stored here as the count made every comparison with it false.
    yield put(lessonActions.updateCurrentLevelTiers(startupTierCountFor(data, levelNumber)));
    const levelTierCount = tierCountOf(yield getTiersForLevel(levelNumber));
    if (levelTierCount !== null) {
      yield put(lessonActions.updateCurrentLevelTiers(levelTierCount));
    }
    const timeSig = (yield gettimeSigForLevel(levelNumber, lesson.tierNumber)) as {
      numerator: number;
      denominator: number;
    };
    yield put(lessonActions.setLessonTimeSigNumerator(timeSig.numerator));
    yield put(lessonActions.setLessonTimeSigDenominator(timeSig.denominator));
  } catch (err) {
    awsRum?.recordError(err);
    logger.error('[onLevelSelectUpdate] level refresh failed', err);
  }
}

function buildULP(
  levelNumber: number,
  playSessionId: string | undefined | null,
  ulp: Partial<UserLevelData>,
) {
  // maybe this should just be dropped on the backend?

  if (ulp && Object.keys(ulp).contains('user_id')) {
    delete ulp.user_id;
  }
  const body = {
    ...ulp,
    level: {
      level_number: levelNumber,
    },
  };
  // explicitly checking for undefined here because "null" is how we explicitly wipe the current session from the
  // ULP of the level. If a user arrives to a level, and a current session is sent from the backend, it can lead to a
  // rerender.
  // I may have fixed that though...
  if (playSessionId !== undefined) {
    body['play_session'] = {
      id: playSessionId,
    };
  }
  return body;
}

export function* onCurrentULPUpdate2(action: UpdateCurrentULP): Generator<unknown, void, unknown> {
  const data: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
  const _events: EventState = yield select(({ eventReducer }) => eventReducer);
  const _lesson: LessonState = yield select(({ lessonReducer }) => lessonReducer);
  // const { jwtToken }: AuthState = yield select(({authReducer}) => authReducer)
  const level = data.levelData[data.levelSelect].level_number; // This will determine which tutorial we grab!
  const toUpdateULP = action.payload.currentUserLevelProgress;
  // instead of just using the current play session id if the given is undefined, we're checking for the key
  // because it can be explicitly set to undefined, which we would want to retain
  const playSessionId = action.payload.currentUserLevelProgress?.play_session?.id;
  // action.payload.currentUserLevelProgress && has(action, "payload.currentUserLevelProgress.play_session.id") ?
  // action.payload.currentUserLevelProgress.play_session?.id :
  // lesson.currentUserLevelData?.play_session?.id
  // const levelProgressExists = find(data.levelData, levelDatum => levelDatum )
  if (!toUpdateULP) {
    return;
  }
  let body = buildULP(level, playSessionId, toUpdateULP);
  let updatedULP: AxiosResponse<UserLevelData> | undefined = undefined;
  try {
    updatedULP = yield axios.patch<UserLevelData>(
      baseUrl + `user-level-progress?levelNumber=${level}`,
      body,
    );
  } catch (err) {
    if (err instanceof AxiosError && err?.response?.status && err.response.status == 404) {
      const update = body;
      body = buildULP(level, playSessionId, StartingULP());
      try {
        updatedULP = yield axios.post<UserLevelData>(
          baseUrl + `user-level-progress?levelNumber=${level}`,
          body,
        );
      } catch (err) {
        // MUS-651: two updates for a level with no progress yet (entering a new
        // level fires several at once) both get the 404 and both create it;
        // the second create is refused with a 422 because the first one made
        // the row. Dropping the update there lost what it carried - the new
        // play session among it, which left the lesson waiting for a session
        // that never came. The row exists now, so send the update to it.
        if (err instanceof AxiosError && err.response?.status === 422) {
          try {
            updatedULP = (yield axios.patch<UserLevelData>(
              baseUrl + `user-level-progress?levelNumber=${level}`,
              update,
            )) as AxiosResponse<UserLevelData>;
          } catch (retryErr) {
            awsRum?.recordError(retryErr);
            console.error(retryErr);
          }
        } else {
          awsRum?.recordError(err);
          console.error(err);
        }
      }
    } else {
      if (err instanceof AxiosError) {
        logger.info(err);
        logger.info('errorstatus', err?.status);
        logger.info('error code', err?.code);
      }

      awsRum?.recordError(err);
      console.error(err);
    }
  } finally {
    if (updatedULP) {
      yield put(lessonActions.updateCurrentULPSuccess({ next: updatedULP.data }));
    }
    //TODO figure out failure path (right now will probably just hang)
  }
}

function* _onCurrentULPUpdate(action: UpdateCurrentULP): Generator<unknown, void, unknown> {
  const data: AppState = yield select(({ mainAppReducer }) => mainAppReducer);
  const _events: EventState = yield select(({ eventReducer }) => eventReducer);
  const lesson: LessonState = yield select(({ lessonReducer }) => lessonReducer);
  // const { jwtToken }: AuthState = yield select(({authReducer}) => authReducer)
  const level = data.levelData[data.levelSelect].level_number; // This will determine which tutorial we grab!
  const toUpdateULP = action.payload.currentUserLevelProgress;
  let updatedULP: AxiosResponse<UserLevelData>;
  let playSessionId;
  if (
    has(action, 'payload.currentUserLevelProgress.play_session.id') &&
    action.payload.currentUserLevelProgress?.play_session?.id === null
  ) {
    playSessionId = action.payload.currentUserLevelProgress?.play_session?.id;
  } else if (!action.payload.currentUserLevelProgress?.play_session?.id) {
    playSessionId = lesson.currentUserLevelData?.play_session?.id;
  } else if (action.payload.currentUserLevelProgress?.play_session?.id) {
    playSessionId = action.payload.currentUserLevelProgress?.play_session?.id;
  }
  if (!toUpdateULP) {
    try {
      updatedULP = yield axios.post<UserLevelData>(
        baseUrl + `user-level-progress?levelNumber=${level}`,
        {
          ...StartingULP(),
          level: {
            level_number: level,
          },
          play_session: {
            id: playSessionId,
          },
        },
      );
      yield put(lessonActions.updateCurrentULPSuccess({ next: updatedULP.data }));
    } catch (err) {
      if (err instanceof AxiosError) {
        awsRum?.recordError(err);
        console.error(err);
      }
    }
    return;
  }

  if (toUpdateULP && Object.keys(toUpdateULP).contains('user_id')) {
    delete toUpdateULP.user_id;
  }
  try {
    updatedULP = yield axios.patch<UserLevelData>(
      baseUrl + `user-level-progress?levelNumber=${level}`,
      {
        level: {
          level_number: level,
        },
        ...toUpdateULP,
        play_session: {
          id: playSessionId,
        },
      },
    );

    yield put(
      lessonActions.updateCurrentULPSuccess({
        next: updatedULP.data,
        prev: lesson.currentUserLevelData || undefined,
      }),
    );
  } catch (err) {
    if (err instanceof AxiosError) {
      if (err?.response?.data?.message === 'User level progress not found.') {
        updatedULP = yield axios.post<UserLevelData>(
          baseUrl + `user-level-progress?levelNumber=${level}`,
          {
            ...StartingULP(),
            level: {
              level_number: level,
            },
            play_session: {
              id: playSessionId,
            },
          },
        );
        // TODO: add play session id to current ulp in backend
        yield put(
          lessonActions.updateCurrentULPSuccess({
            next: updatedULP.data,
            prev: lesson.currentUserLevelData || undefined,
          }),
        );
      }
    } else {
      awsRum?.recordError(err);
      console.error(err);
    }
  }
}

function* onCurrentULPSuccess({ payload }: UpdateCurrentULPSuccess) {
  const { userLevelData } = yield select(({ mainAppReducer }) => mainAppReducer);
  const updatedArray = userLevelData.some((item: UserLevelData) => item.id === payload.next.id)
    ? userLevelData.map((item: UserLevelData) =>
        item.id === payload.next.id ? payload.next : item,
      )
    : [...userLevelData, payload.next];
  logger.debug('updated user level progress array');
  logger.debug(updatedArray);
  yield put(appActions.fetchUserLevelsSuccess({ userLevelData: updatedArray }));
}

export async function getPlaySessionSummary(playSessionId: string) {
  const options = {
    url: process.env.REACT_APP_BACKEND_URL + `/api/v1/play-session/summary/${playSessionId}`,
    headers: {
      'Access-Control-Allow-Origin': '*',
    },
    timeout: 5000,
  };
  // await new Promise (res => setTimeout(res, 1000))
  const response = await axios.request(options);
  return (response as { data: unknown })['data'];
}

export function* onInitWrapUp(action?: InitiateWrapUp): Generator<unknown, void, unknown> {
  const lesson: LessonState = yield select(({ lessonReducer }) => lessonReducer);
  // MUS-651: a wrap-up asked for a won tier's session (the tier-complete flush,
  // or the lesson's Retry) is not for a session the lesson has since left, and
  // does not reopen a wrap-up already open: a flush that answered late put the
  // wrap-up back on its first page, or fetched the next session's empty summary.
  const wonTierSessionId = action?.payload?.playSessionId;
  if (wonTierSessionId) {
    if (wonTierSessionId !== lesson.currentUserLevelData?.play_session?.id) {
      logger.info('[onInitWrapUp] wrap-up for a session the lesson has left; ignored');
      return;
    }
    if (lesson.showWrapUp) {
      logger.info('[onInitWrapUp] wrap-up already open; ignored');
      return;
    }
  }
  yield put(lessonActions.setLessonProcessPending(true));
  if (lesson.currentUserLevelData?.play_session?.id) {
    logger.info('about to get session summay');
    try {
      const playSessionSummary: PlaySessionData = yield getPlaySessionSummary(
        lesson.currentUserLevelData?.play_session.id,
      );
      logger.info('play session summary', playSessionSummary);
      if (!playSessionSummary.played_notes) {
        logger.info('throwing play session data error');
        throw new Error('play session data error');
      }
      yield put(lessonActions.setTierSummary(playSessionSummary));
      yield put(appActions.invalidateDashboardCache());
      logger.info('setting show wrap up to true');
      yield put(lessonActions.setShowWrapUp(true));
      yield put(lessonActions.setWrapUpIndex(0));
      yield put(lessonActions.setLessonProcessPending(false));
    } catch (_err) {
      logger.info('setting summary request error to true');
      yield put(lessonActions.setTierSummaryRequestError(true));
      yield put(lessonActions.setLessonProcessPending(false));
    }
  } else {
    // MUS-651: with no play session there is no summary to wait for. Left
    // pending, the tier-complete screen waited on it forever; the summary
    // error path takes the user back to the roadmap instead.
    logger.warn('[onInitWrapUp] no play session to summarize');
    yield put(lessonActions.setTierSummaryRequestError(true));
    yield put(lessonActions.setLessonProcessPending(false));
  }
}

// MUS-651: a play-session request that has not answered in this long fails,
// so the lesson's Retry is never held up for good behind one that will not
// answer. It is longer than the load balancer's idle timeout (60 s, the AWS
// default; no answer can come later than that), so an answer the server sends
// is never given up on: given up on, the session it made was left as an empty
// row while Retry made a second one.
export const PLAY_SESSION_REQUEST_TIMEOUT_MS = 65000;

// MUS-651: counts the times the sessions being created were abandoned: the
// lesson screen was left, or the lesson's Retry started the tier over while a
// request hung. A session asked for before that is not posted if it has not
// been yet, and not taken up if it answers after: it would become the
// progress row's session for a lesson that has moved on.
let sessionRequestsAbandoned = 0;
export const abandonLevelSessionsBeingCreated = (): void => {
  sessionRequestsAbandoned += 1;
};
export const leftLesson = abandonLevelSessionsBeingCreated;

// MUS-651: how many play sessions are being created, counted from the saga's
// entry (its tiers request included) to its end. The lesson's Retry no longer
// waits on one (a hanging request held it for the whole timeout); it abandons
// it instead (abandonLevelSessionsBeingCreated), so the two cannot race.
let levelSessionsBeingCreated = 0;
export const isLevelSessionBeingCreated = (): boolean => levelSessionsBeingCreated > 0;

async function postSession(
  body:
    | { level: { level_number: number }; tier_number: number; analytics_session_id?: string }
    | { repertoire: { id: number } },
) {
  return axios.post(baseUrl + 'play-session', body, {
    timeout: PLAY_SESSION_REQUEST_TIMEOUT_MS,
  });
}

export function* createLevelSession(action: CreateLevelSession): Generator<unknown, void, unknown> {
  levelSessionsBeingCreated += 1;
  try {
    yield* openLevelSession(action);
  } finally {
    levelSessionsBeingCreated -= 1;
  }
}

function* openLevelSession(action: CreateLevelSession): Generator<unknown, void, unknown> {
  console.log('createLevelSession entered', action.payload);
  logger.info('create level session entered');
  const abandonedAtRequest = sessionRequestsAbandoned;
  const abandoned = () => sessionRequestsAbandoned !== abandonedAtRequest;

  let validatedTierNumber = action.payload.tier_number;
  try {
    const levelTierCount = tierCountOf(yield getTiersForLevel(action.payload.level_number));
    const maxTiers = levelTierCount ?? 1;

    // Clamp tier number to valid range [1, maxTiers]
    if (!validatedTierNumber || isNaN(validatedTierNumber) || validatedTierNumber < 1) {
      validatedTierNumber = 1;
      logger.warn(`Invalid tier number ${action.payload.tier_number}, defaulting to 1`);
    } else if (validatedTierNumber > maxTiers) {
      validatedTierNumber = maxTiers;
      logger.warn(
        `Tier number ${action.payload.tier_number} exceeds max ${maxTiers}, clamping to ${maxTiers}`,
      );
    }

    // Also update the currentLevelTiers in case it wasn't set. Not for a
    // session abandoned meanwhile: the lesson may be on another level now.
    if (levelTierCount !== null && !abandoned()) {
      yield put(lessonActions.updateCurrentLevelTiers(levelTierCount));
    }
  } catch (tierErr) {
    // MUS-651: without the tier list the session opens at the requested tier
    // (whole, at least 1), not at tier 1. Tier 1 put a player on a later tier
    // back at the start and wrote that into their progress. Every caller asks
    // for a tier the player has reached or unlocked, and the same tier is
    // written to current_tier below, so the session, the progress and the
    // tier the lesson plays and completes all agree. It is not limited by
    // currentLevelTiers, the stored count this request was meant to refresh.
    // It is capped at the level's tier count loaded at startup (tiersByLevels),
    // when this run of the app loaded it; a copy stored by an earlier run can
    // be missing tiers added since.
    const requestedTier = Math.floor(Number(action.payload.tier_number));
    const wholeRequestedTier =
      Number.isFinite(requestedTier) && requestedTier >= 1 ? requestedTier : 1;
    const app = (yield select((state: { mainAppReducer?: AppState }) => state.mainAppReducer)) as
      | AppState
      | undefined;
    const startupTierCount = startupTierCountFor(app, action.payload.level_number);
    validatedTierNumber =
      startupTierCount !== null
        ? Math.min(wholeRequestedTier, startupTierCount)
        : wholeRequestedTier;
    // The lesson's count, when it has none, is that startup count too: without
    // it the level's last tier could never complete it.
    const lessonTierCount = (yield select(
      (state: { lessonReducer?: LessonState }) => state.lessonReducer?.currentLevelTiers,
    )) as LessonState['currentLevelTiers'] | undefined;
    if (startupTierCount !== null && tierCountOf(lessonTierCount) === null && !abandoned()) {
      yield put(lessonActions.updateCurrentLevelTiers(startupTierCount));
    }
    logger.error(
      `Failed to get tiers for level, proceeding with tier ${validatedTierNumber}`,
      tierErr,
    );
  }

  const { sessionId } = (yield select(({ eventReducer }) => eventReducer)) as EventState;
  const graded = (yield select(selectEffectiveGrading)) as boolean;
  const body = {
    tier_number: validatedTierNumber,
    level: {
      level_number: action.payload.level_number,
    },
    error_recognition_mode: action.payload.error_recognition_mode,
    // The app sitting the session opens in.
    analytics_session_id: sessionId,
    // Ungraded play marks its session (see Utils/playAlong/effectiveGrading).
    ...ungradedSessionFields(graded),
  };
  if (abandoned()) {
    logger.warn('[createLevelSession] abandoned before it was posted');
    return;
  }
  try {
    const playSession = yield postSession(body);
    logger.info('play session created', playSession?.data?.id);
    if (abandoned()) {
      logger.warn('[createLevelSession] session answered after it was abandoned; not taken up');
      return;
    }
    yield put(appActions.invalidateDashboardCache());
    yield put(
      lessonActions.updateCurrentULP({
        currentUserLevelProgress: {
          current_tier: validatedTierNumber,
          level: {
            level_number: action.payload.level_number,
          },
          play_session: { id: playSession?.data?.id },
        },
      }),
    );
    yield put(lessonActions.setTierSummary(null));
    yield put(lessonActions.createLevelSessionSuccess(action.payload));
  } catch (err) {
    console.error(err);
    if (err instanceof AxiosError && err?.response?.status && err.response.status > 499) {
      awsRum?.recordError(err);
    } else if (!(err instanceof AxiosError)) {
      awsRum?.recordError(err);
    }
  }
}

function* onCreateLevelSessionSuccess(_action: CreateLevelSession) {
  // yield put(eventActions.tierStartedEventAction(action.payload.level_number, action.payload.tier_number));
}

export default [
  takeEvery(lessonActionTypes.UPDATE_CURRENT_ULP, onCurrentULPUpdate2),
  // MUS-651: one wrap-up request at a time; another that comes while one is
  // being answered (a late flush beside a Retry) is dropped, not answered twice.
  takeLeading(lessonActionTypes.INITIATE_WRAP_UP, onInitWrapUp),
  // takeEvery(EventActionTypes.DISPATCH_EVENTS_SUCCESS, onDispatchEventsSuccess),
  takeEvery(lessonActionTypes.CREATE_LEVEL_SESSION, createLevelSession),
  takeEvery(lessonActionTypes.UPDATE_CURRENT_ULP_SUCCESS, onCurrentULPSuccess),
  takeEvery(lessonActionTypes.CREATE_LEVEL_SESSION_SUCCESS, onCreateLevelSessionSuccess),
  takeEvery(actionTypes.SET_LEVEL_SELECT, onLevelSelectUpdate),
];
